import { IoIosCheckmarkCircle } from 'react-icons/io';
import { MdClose, MdError } from 'react-icons/md';
import { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '@/components/Button';
import Select from '@/components/Select';
import Input from '@/components/Input';
import { CandidateImportMappingRow } from './CandidateImportMappingRow';
import { SavePresetDialog } from './SavePresetDialog';
import { PresetDiffDialog } from './PresetDiffDialog';
import type {
  AgentApiCandidateImportRequestsShow,
  SharedCsvFieldMapping,
  SharedCsvDefaultMapping,
  SharedCsvNameMapping,
} from '@/types/serializers';
import { useUpdateCandidateImportRequest } from '../api/useUpdateCandidateImportRequest';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useFetchCsvMappingTemplates from '../api/useFetchCsvMappingTemplates';
import useFetchCsvMappingTemplate from '../api/useFetchCsvMappingTemplate';

interface Props {
  importRequest: AgentApiCandidateImportRequestsShow;
  encryptId: string;
  onSubmitSuccess: () => void;
  onShowPresetSave: (
    fieldMappings: Record<number, number[]>,
    defaultMappings: Record<number, string>,
    selectedOrganizationUserId: number | null,
    nameCsvIndices: number[],
    nameDefaultValue: string,
    presetTitle?: string
  ) => void;
}

export function CandidateImportMappingView({
  importRequest,
  encryptId,
  onSubmitSuccess,
  onShowPresetSave,
}: Props) {
  const updateMutation = useUpdateCandidateImportRequest();
  const { data: activeUsersData } = useActiveUsers();
  const { data: templatesData } = useFetchCsvMappingTemplates();

  // 選択されたプリセットIDの状態管理（初期値はimportRequestから）
  const [selectedPresetId, setSelectedPresetId] = useState<string>(
    importRequest.csv_mapping_template?.id.toString() || ''
  );

  // プリセート詳細の取得（選択時のみ）
  const { data: selectedPresetData } = useFetchCsvMappingTemplate(selectedPresetId);

  // importRequestのマッピング情報を初期値として設定
  const [fieldMappings, setFieldMappings] = useState<Record<number, number[]>>(() => {
    const initialMappings: Record<number, number[]> = {};
    if (importRequest.field_mapping && Array.isArray(importRequest.field_mapping)) {
      importRequest.field_mapping.forEach((mapping) => {
        const fieldMapping = mapping as SharedCsvFieldMapping;
        if (fieldMapping.custom_field_id && fieldMapping.csv_index) {
          initialMappings[fieldMapping.custom_field_id] = fieldMapping.csv_index;
        }
      });
    }
    return initialMappings;
  });

  const [defaultMappings, setDefaultMappings] = useState<Record<number, string>>(() => {
    const initialMappings: Record<number, string> = {};
    if (importRequest.default_mapping && Array.isArray(importRequest.default_mapping)) {
      importRequest.default_mapping.forEach((mapping) => {
        const defaultMapping = mapping as SharedCsvDefaultMapping;
        if (defaultMapping.custom_field_id && defaultMapping.default_value) {
          initialMappings[defaultMapping.custom_field_id] = defaultMapping.default_value;
        }
      });
    }
    return initialMappings;
  });
  const [nameCsvIndices, setNameCsvIndices] = useState<number[]>([0]);
  const [nameDefaultValue, setNameDefaultValue] = useState<string>('');

  const [selectedOrganizationUserId, setSelectedOrganizationUserId] = useState<number | null>(null);
  const [assigneeSearchText, setAssigneeSearchText] = useState<string>('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDiffDialog, setShowDiffDialog] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const assigneeInputRef = useRef<HTMLDivElement>(null);

  // プリセートとの差分をチェック（選択されたプリセートとの比較）
  const hasDiffFromPreset = useMemo(() => {
    if (!selectedPresetId || !selectedPresetData) return false;

    // 選択されたプリセートのマッピング情報を構築
    const presetFieldMappings: Record<number, number[]> = {};
    if (selectedPresetData.field_mapping && Array.isArray(selectedPresetData.field_mapping)) {
      selectedPresetData.field_mapping.forEach((mapping) => {
        const fieldMapping = mapping as SharedCsvFieldMapping;
        if (fieldMapping.custom_field_id && fieldMapping.csv_index) {
          presetFieldMappings[fieldMapping.custom_field_id] = fieldMapping.csv_index;
        }
      });
    }

    const presetDefaultMappings: Record<number, string> = {};
    if (selectedPresetData.default_mapping && Array.isArray(selectedPresetData.default_mapping)) {
      selectedPresetData.default_mapping.forEach((mapping) => {
        const defaultMapping = mapping as SharedCsvDefaultMapping;
        if (defaultMapping.custom_field_id && defaultMapping.default_value) {
          presetDefaultMappings[defaultMapping.custom_field_id] = defaultMapping.default_value;
        }
      });
    }

    const presetNameCsvIndices =
      selectedPresetData.name_mapping && Array.isArray(selectedPresetData.name_mapping)
        ? selectedPresetData.name_mapping
            .map((mapping) => (mapping as SharedCsvNameMapping).csv_index)
            .filter((index): index is number => typeof index === 'number')
        : [];

    // field_mappings の差分チェック
    const fieldKeys = new Set([...Object.keys(presetFieldMappings), ...Object.keys(fieldMappings)]);
    for (const key of fieldKeys) {
      const numKey = Number(key);
      const presetValue = presetFieldMappings[numKey] || [];
      const currentValue = fieldMappings[numKey] || [];
      if (
        JSON.stringify([...presetValue].sort((a, b) => a - b)) !==
        JSON.stringify([...currentValue].sort((a, b) => a - b))
      ) {
        return true;
      }
    }

    // default_mappings の差分チェック
    const defaultKeys = new Set([
      ...Object.keys(presetDefaultMappings),
      ...Object.keys(defaultMappings),
    ]);
    for (const key of defaultKeys) {
      const numKey = Number(key);
      if ((presetDefaultMappings[numKey] || '') !== (defaultMappings[numKey] || '')) {
        return true;
      }
    }

    // name_mappings の差分チェック
    if (
      JSON.stringify([...presetNameCsvIndices].sort((a, b) => a - b)) !==
      JSON.stringify([...nameCsvIndices].sort((a, b) => a - b))
    ) {
      return true;
    }

    return false;
  }, [fieldMappings, defaultMappings, nameCsvIndices, selectedPresetId, selectedPresetData]);

  // エラーチェック：必須項目でCSV列も固定値も設定されていないものがあるか
  const hasValidationErrors = useMemo(() => {
    return importRequest.custom_fields?.some((field) => {
      if (!field.is_required) return false;
      const hasCsvMapping = fieldMappings[field.id]?.length > 0;
      const hasDefaultValue = defaultMappings[field.id]?.trim().length > 0;
      return !hasCsvMapping && !hasDefaultValue;
    });
  }, [importRequest.custom_fields, fieldMappings, defaultMappings]);

  const allCustomFieldUsedIndices = useMemo(
    () => Object.values(fieldMappings).flatMap((indices) => indices),
    [fieldMappings]
  );

  // 各フィールドで選択済みのCSVインデックスを集計（自分自身は除外）
  const getDisabledIndicesForField = (fieldId: number) => {
    const allUsedIndices: number[] = [];
    // 氏名で使用中のインデックス
    nameCsvIndices.forEach((idx) => allUsedIndices.push(idx));
    // 他のカスタムフィールドで使用中のインデックス
    Object.entries(fieldMappings).forEach(([id, indices]) => {
      if (Number(id) !== fieldId) {
        indices.forEach((idx) => allUsedIndices.push(idx));
      }
    });
    return Array.from(new Set(allUsedIndices));
  };

  const getDisabledIndicesForName = () => {
    const allUsedIndices: number[] = [];
    allCustomFieldUsedIndices.forEach((idx) => allUsedIndices.push(idx));
    return Array.from(new Set(allUsedIndices));
  };

  // 担当者ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assigneeInputRef.current && !assigneeInputRef.current.contains(event.target as Node)) {
        setShowAssigneeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFieldMappingChange = (customFieldId: number, csvIndices: number[]) => {
    setFieldMappings((prev) => ({
      ...prev,
      [customFieldId]: csvIndices,
    }));
  };

  const handleDefaultMappingChange = (customFieldId: number, value: string) => {
    setDefaultMappings((prev) => ({
      ...prev,
      [customFieldId]: value,
    }));
  };

  // プリセット選択時のハンドラー
  const handlePresetChange = (presetId: string) => {
    if (!presetId) {
      // プリセット選択解除
      setSelectedPresetId('');
      setFieldMappings({});
      setDefaultMappings({});
      setNameCsvIndices([0]);
      setNameDefaultValue('');
      return;
    }

    // プリセートを選択
    setSelectedPresetId(presetId);
  };

  // プリセート詳細取得後、マッピングを適用
  useEffect(() => {
    if (!selectedPresetData) return;

    // ヘッダー一致チェック
    const currentHeaders = importRequest.header_array || [];
    const presetHeaders = selectedPresetData.header_array || [];

    if (JSON.stringify(currentHeaders) !== JSON.stringify(presetHeaders)) {
      alert('CSVの構造が異なります。このプリセットは適用できません。');
      setSelectedPresetId(importRequest.csv_mapping_template?.id.toString() || '');
      return;
    }

    // field_mappings を適用
    const newFieldMappings: Record<number, number[]> = {};
    if (selectedPresetData.field_mapping && Array.isArray(selectedPresetData.field_mapping)) {
      selectedPresetData.field_mapping.forEach((mapping) => {
        const fieldMapping = mapping as SharedCsvFieldMapping;
        if (fieldMapping.custom_field_id && fieldMapping.csv_index) {
          newFieldMappings[fieldMapping.custom_field_id] = fieldMapping.csv_index;
        }
      });
    }
    setFieldMappings(newFieldMappings);

    // default_mappings を適用
    const newDefaultMappings: Record<number, string> = {};
    if (selectedPresetData.default_mapping && Array.isArray(selectedPresetData.default_mapping)) {
      selectedPresetData.default_mapping.forEach((mapping) => {
        const defaultMapping = mapping as SharedCsvDefaultMapping;
        if (defaultMapping.custom_field_id && defaultMapping.default_value) {
          newDefaultMappings[defaultMapping.custom_field_id] = defaultMapping.default_value;
        }
      });
    }
    setDefaultMappings(newDefaultMappings);

    // name_mappings を適用
    if (selectedPresetData.name_mapping && Array.isArray(selectedPresetData.name_mapping)) {
      const nameIndices = selectedPresetData.name_mapping
        .map((mapping) => (mapping as SharedCsvNameMapping).csv_index)
        .filter((index): index is number => typeof index === 'number');
      setNameCsvIndices(nameIndices.length > 0 ? nameIndices : [0]);
    }
  }, [selectedPresetData, importRequest.header_array, importRequest.csv_mapping_template?.id]);

  const handleSubmit = async () => {
    // バリデーションエラーチェック
    if (hasValidationErrors) {
      setShowValidationError(true);
      return;
    }

    // エラーメッセージを非表示
    setShowValidationError(false);

    // プリセット選択済みの場合
    if (selectedPresetId) {
      // 差分がある場合は差分ダイアログを表示
      if (hasDiffFromPreset) {
        setShowDiffDialog(true);
        return;
      }
      // 差分がない場合は直接取り込み（プリセートで上書き保存）
      await handleImportWithSelectedPreset();
      return;
    }

    // プリセット未選択の場合はダイアログを表示
    setShowSaveDialog(true);
  };

  const handleImportWithoutSave = async () => {
    try {
      const field_mappings = Object.entries(fieldMappings).map(([customFieldId, csvIndex]) => ({
        custom_field_id: Number(customFieldId),
        csv_index: csvIndex,
      }));

      const name_mappings = nameCsvIndices.map((idx) => ({
        csv_index: idx,
        csv_header: importRequest.header_array?.[idx] || '',
        default_value: '',
      }));

      const default_mappings = Object.entries(defaultMappings)
        .filter(([, value]) => value)
        .map(([customFieldId, value]) => ({
          custom_field_id: Number(customFieldId),
          default_value: value,
        }));

      await updateMutation.mutateAsync({
        encryptId,
        field_mappings,
        name_mappings,
        default_mappings,
        organization_user_id: selectedOrganizationUserId,
      });

      setShowSaveDialog(false);
      onSubmitSuccess();
    } catch (error) {
      alert(error instanceof Error ? error.message : '更新に失敗しました。');
    }
  };

  const handleSaveAndImport = () => {
    setShowSaveDialog(false);
    onShowPresetSave(
      fieldMappings,
      defaultMappings,
      selectedOrganizationUserId,
      nameCsvIndices,
      nameDefaultValue
    );
  };

  const handleImportWithSelectedPreset = async () => {
    try {
      const field_mappings = Object.entries(fieldMappings).map(([customFieldId, csvIndex]) => ({
        custom_field_id: Number(customFieldId),
        csv_index: csvIndex,
      }));

      const name_mappings = nameCsvIndices.map((idx) => ({
        csv_index: idx,
        csv_header: importRequest.header_array?.[idx] || '',
        default_value: '',
      }));

      const default_mappings = Object.entries(defaultMappings)
        .filter(([, value]) => value)
        .map(([customFieldId, value]) => ({
          custom_field_id: Number(customFieldId),
          default_value: value,
        }));

      // マッピング更新
      await updateMutation.mutateAsync({
        encryptId,
        field_mappings,
        name_mappings,
        default_mappings,
        organization_user_id: selectedOrganizationUserId,
      });

      onSubmitSuccess();
    } catch (error) {
      alert(error instanceof Error ? error.message : '更新に失敗しました。');
    }
  };

  const handleOverwriteAndImport = () => {
    // 選択されたプリセートのタイトルを取得
    const selectedPreset = templatesData?.records.find((t) => t.id.toString() === selectedPresetId);

    // プリセート保存画面を表示（タイトルが入力された状態）
    setShowDiffDialog(false);
    onShowPresetSave(
      fieldMappings,
      defaultMappings,
      selectedOrganizationUserId,
      nameCsvIndices,
      nameDefaultValue,
      selectedPreset?.title || ''
    );
  };

  return (
    <div className="rounded-sm bg-white p-lg shadow-layer1">
      <div className="space-y-lg">
        {/* ヘッダー */}
        <h2 className="border-b border-border text-heading-md">取り込みの詳細</h2>

        {/* エラーメッセージバナー */}
        {showValidationError && hasValidationErrors && (
          <div className="bg-error-light flex items-center gap-2 rounded border border-solid border-danger px-md py-3">
            <MdError className="size-5 shrink-0 text-danger" />
            <p className="text-body-sm text-main">項目を指定するか、固定値を入力してください</p>
          </div>
        )}

        {/* 設定セクション */}
        <div className="flex items-end gap-10">
          {/* 取り込みプリセット */}
          <div className="flex flex-col gap-2">
            <label className="text-heading-xxs">取り込みプリセット</label>
            <div className="w-[236px]">
              <Select
                value={selectedPresetId}
                onValueChange={handlePresetChange}
                defaultLabel="プリセットを選択"
                items={
                  templatesData?.records.map((template) => ({
                    value: template.id.toString(),
                    label: template.title,
                  })) || []
                }
              />
            </div>
          </div>

          {/* 担当者 */}
          <div className="relative flex flex-col gap-2" ref={assigneeInputRef}>
            <label className="text-heading-xxs">担当者を割り当て</label>
            <Input
              type="text"
              value={assigneeSearchText}
              onChange={(e) => {
                setAssigneeSearchText(e.target.value);
                setShowAssigneeDropdown(true);
              }}
              onFocus={() => setShowAssigneeDropdown(true)}
              placeholder="担当者名"
              className="w-[236px]"
              search
            />
            {showAssigneeDropdown && (
              <div className="absolute top-full z-10 mt-1 w-[236px] rounded border border-border bg-white shadow-lg">
                <div className="hidden-scrollbar max-h-[240px] overflow-y-auto">
                  {activeUsersData?.activeUsers
                    .filter((user) =>
                      assigneeSearchText
                        ? user.name.toLowerCase().includes(assigneeSearchText.toLowerCase())
                        : true
                    )
                    .map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="flex h-xxl w-full cursor-pointer items-center px-3 hover:bg-gray"
                        onClick={() => {
                          setSelectedOrganizationUserId(user.id);
                          setAssigneeSearchText(user.name);
                          setShowAssigneeDropdown(false);
                        }}
                      >
                        <span>{user.name}</span>
                        {selectedOrganizationUserId === user.id && (
                          <IoIosCheckmarkCircle size={20} className="ml-auto text-primary" />
                        )}
                      </button>
                    ))}
                  {activeUsersData?.activeUsers.filter((user) =>
                    assigneeSearchText
                      ? user.name.toLowerCase().includes(assigneeSearchText.toLowerCase())
                      : true
                  ).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-400">
                      該当する担当者が見つかりません
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {(selectedPresetId || selectedOrganizationUserId) && (
            <Button
              color="tertiary"
              size="lg"
              type="button"
              onClick={() => {
                if (selectedPresetId) handlePresetChange('');
                if (selectedOrganizationUserId) {
                  setSelectedOrganizationUserId(null);
                  setAssigneeSearchText('');
                }
              }}
            >
              <MdClose size={24} className="text-primary" />
              選択をクリア
            </Button>
          )}
        </div>

        {/* データの割り当て */}
        <div className="flex flex-col gap-xs">
          <h3 className="text-heading-xxs">データの割り当て</h3>

          <div className="flex flex-col">
            {/* ヘッダー行 */}
            <div className="flex h-[28px] items-end gap-md bg-gray px-4 pb-[3px]">
              <div className="w-[132px]">
                <p className="text-heading-3xs text-sub">エージェントハブ</p>
              </div>
              <div className="h-1 w-[160px]" />
              <div className="h-1 w-6" />
              <div className="flex-1">
                <p className="text-heading-3xs text-sub">CSV</p>
              </div>
            </div>

            {/* 情報タイプヘッダー */}
            <div className="flex h-xxl items-center gap-md border-b border-solid border-border bg-gray p-md">
              <div className="w-[132px]">
                <p className="text-heading-xxs text-sub">情報タイプ</p>
              </div>
              <div className="w-[160px]">
                <p className="text-heading-xxs text-sub">割り当て先</p>
              </div>
              <div className="invisible w-6" />
              <div className="flex-1">
                <p className="text-heading-xxs text-sub">CSV項目/上書きする</p>
              </div>
            </div>

            {/* 氏名行（固定・必須） */}
            <CandidateImportMappingRow
              customField={{
                id: -1,
                name: '氏名',
                field_type: 'short_text',
                is_required: true,
                unit_name: null,
                order_number: 0,
                alias_id: null,
                published_forms_count: 0,
                custom_field_options: [],
                custom_category: { id: 0, name: '', order_number: 0 },
              }}
              csvHeaders={importRequest.header_array}
              selectedCsvIndices={nameCsvIndices}
              disabledCsvIndices={getDisabledIndicesForName()}
              defaultValue={nameDefaultValue}
              onCsvIndicesChange={(indices) => setNameCsvIndices(indices)}
              onDefaultValueChange={(value) => setNameDefaultValue(value)}
              showValidationError={false}
            />

            {/* マッピング行 */}
            {importRequest.custom_fields?.map((field) => (
              <CandidateImportMappingRow
                key={field.id}
                customField={field}
                csvHeaders={importRequest.header_array}
                selectedCsvIndices={fieldMappings[field.id] || []}
                defaultValue={defaultMappings[field.id] || ''}
                onCsvIndicesChange={(indices: number[]) =>
                  handleFieldMappingChange(field.id, indices)
                }
                onDefaultValueChange={(value: string) =>
                  handleDefaultMappingChange(field.id, value)
                }
                disabledCsvIndices={getDisabledIndicesForField(field.id)}
                showValidationError={showValidationError}
              />
            ))}
          </div>
        </div>

        {/* 取り込みボタン */}
        <div className="flex justify-end">
          <Button
            color="primary"
            size="lg"
            type="button"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '処理中...' : '取り込み'}
          </Button>
        </div>
      </div>

      {/* プリセット保存確認ダイアログ */}
      <SavePresetDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onSaveAndImport={handleSaveAndImport}
        onImportWithoutSave={handleImportWithoutSave}
      />

      {/* プリセット差分確認ダイアログ */}
      <PresetDiffDialog
        open={showDiffDialog}
        onOpenChange={setShowDiffDialog}
        onOverwriteAndImport={handleOverwriteAndImport}
        onImportWithoutOverwrite={handleImportWithoutSave}
      />
    </div>
  );
}
